// Write in a lift that isn't in Grit, or change a lift's reps and rest (Kenny, 2026-11-02).
// mode 'new' asks everything; mode 'edit' only asks numbers. onSave(values) gets
// { name, muscle, equipment, tracking, sets, lo, hi, rest, inc }.
import { escapeHtml } from '../lib/exercise-names.js';
import { icon } from './icons.js';

const MUSCLES = ['chest', 'back', 'shoulders', 'biceps', 'triceps', 'quads', 'hamstrings', 'glutes', 'calves', 'abs'];
const TYPES = [['dumbbell', 'Dumbbell'], ['barbell', 'Barbell'], ['machine', 'Machine'], ['cable', 'Cable'], ['bodyweight', 'Bodyweight'], ['timed', 'Timed hold']];
const JUMP = { dumbbell: 5, cable: 5, barbell: 10, machine: 10 };
const REST_OPTS = [60, 90, 120, 180, 240];
const fmtRest = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
const cap = (s) => s[0].toUpperCase() + s.slice(1);

export function openLiftForm({ mode = 'new', title = 'Write in a lift', saveLabel = 'Save', name = '', muscle = 'chest', type = 'dumbbell', tracking = 'weight', sets = 3, lo = 6, hi = 8, rest = 120, inc = null, onSave }) {
  const isNew = mode === 'new';
  let curRest = rest;
  let curType = type;
  const trackingNow = () => (isNew ? (curType === 'timed' ? 'time' : curType === 'bodyweight' ? 'bodyweight' : 'weight') : tracking);

  const overlay = document.createElement('div');
  overlay.className = 'fixed inset-0 z-[140] flex items-end justify-center bg-black/30';
  document.body.appendChild(overlay);
  const close = () => overlay.remove();
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

  const num = (id, label, val) => `<label class="flex flex-col gap-1"><input id="${id}" type="number" inputmode="numeric" value="${val ?? ''}" class="grit-input text-center py-2.5 font-bold" /><span class="text-[13px] text-inksoft text-center">${label}</span></label>`;
  const read = (id) => { const v = overlay.querySelector(`#${id}`)?.value; return v === '' || v == null ? null : Number(v); };

  function paint() {
    // Keep what's been typed when the type or rest buttons repaint the sheet.
    if (overlay.firstChild) {
      name = overlay.querySelector('#lf-name')?.value ?? name;
      muscle = overlay.querySelector('#lf-muscle')?.value ?? muscle;
      sets = read('lf-sets') ?? sets; lo = read('lf-lo') ?? lo; hi = read('lf-hi') ?? hi; inc = read('lf-inc') ?? inc;
    }
    const t = trackingNow();
    const unit = t === 'time' ? 'sec' : 'reps';
    overlay.innerHTML = `<div class="grit-sheet overflow-auto">
      <div class="grit-sheet-handle"></div>
      <div class="flex items-center justify-between mb-2">
        <div class="text-[20px] font-bold tracking-tight text-ink truncate">${escapeHtml(title)}</div>
        <button data-close aria-label="Close" class="text-inksoft w-11 h-11 -mr-2 flex-none flex items-center justify-center">${icon.x('w-5 h-5')}</button>
      </div>
      ${isNew ? `<label class="block"><span class="text-[13px] font-bold text-inksoft block mb-1">Name</span><input id="lf-name" class="grit-input" placeholder="e.g. Chest Fly" value="${escapeHtml(name)}" /></label>
      <div class="grid grid-cols-2 gap-2 mt-3">
        <label class="block"><span class="text-[13px] font-bold text-inksoft block mb-1">Muscle</span><select id="lf-muscle" class="grit-input">${MUSCLES.map((m) => `<option value="${m}" ${m === muscle ? 'selected' : ''}>${cap(m)}</option>`).join('')}</select></label>
        <label class="block"><span class="text-[13px] font-bold text-inksoft block mb-1">Type</span><select id="lf-type" class="grit-input">${TYPES.map(([v, l]) => `<option value="${v}" ${v === curType ? 'selected' : ''}>${l}</option>`).join('')}</select></label>
      </div>` : ''}
      <div class="grid ${isNew ? 'grid-cols-3' : 'grid-cols-2'} gap-2 mt-3">
        ${isNew ? num('lf-sets', 'Sets', sets) : ''}${num('lf-lo', `Min ${unit}`, lo)}${num('lf-hi', `Max ${unit}`, hi)}
      </div>
      <div class="mt-3">
        <div class="text-[13px] font-bold text-inksoft mb-1.5">Rest between sets</div>
        <div class="flex gap-1.5">${REST_OPTS.map((s) => `<button data-rest="${s}" class="flex-1 rounded-full min-h-[44px] text-[15px] font-semibold active:scale-95 transition-transform ${curRest === s ? 'bg-ink text-white' : 'bg-fill text-ink'}">${fmtRest(s)}</button>`).join('')}</div>
        <label class="flex items-center justify-between gap-3 mt-2"><span class="text-[15px] text-inksoft">Or exact seconds</span><input id="lf-rest" type="number" inputmode="numeric" value="${curRest}" class="grit-input !w-24 text-center py-2 font-bold" aria-label="Rest in seconds" /></label>
      </div>
      ${t === 'weight' ? `<label class="flex items-center justify-between gap-3 mt-2"><span class="text-[15px] text-inksoft">Weight jump (lb)</span><input id="lf-inc" type="number" inputmode="decimal" value="${inc ?? JUMP[curType] ?? 5}" class="grit-input !w-24 text-center py-2 font-bold" aria-label="Weight jump in pounds" /></label>` : ''}
      <div id="lf-err" class="text-[15px] text-danger mt-2 hidden"></div>
      <button id="lf-save" class="grit-btn mt-4">${escapeHtml(saveLabel)}</button>
    </div>`;

    overlay.querySelector('[data-close]').onclick = close;
    overlay.querySelectorAll('[data-rest]').forEach((b) => { b.onclick = () => { curRest = Number(b.dataset.rest); paint(); }; });
    // No repaint here: on a phone this fires as the Save tap lands, and a repaint would swallow the tap.
    overlay.querySelector('#lf-rest').onchange = (e) => {
      const v = Number(e.target.value);
      if (!(v > 0)) return;
      curRest = Math.round(v);
      overlay.querySelectorAll('[data-rest]').forEach((b) => {
        const on = Number(b.dataset.rest) === curRest;
        b.classList.toggle('bg-ink', on); b.classList.toggle('text-white', on); b.classList.toggle('bg-fill', !on); b.classList.toggle('text-ink', !on);
      });
    };
    const ty = overlay.querySelector('#lf-type');
    if (ty) ty.onchange = () => { curType = ty.value; inc = null; paint(); };
    overlay.querySelector('#lf-save').onclick = save;
  }

  function fail(msg, focusId) {
    const e = overlay.querySelector('#lf-err');
    e.textContent = msg; e.classList.remove('hidden');
    if (focusId) overlay.querySelector(`#${focusId}`)?.focus();
  }

  function save() {
    const n = isNew ? overlay.querySelector('#lf-name').value.trim() : name;
    if (isNew && !n) { fail('Give the lift a name.', 'lf-name'); return; }
    const s = isNew ? Math.round(read('lf-sets')) : sets;
    let a = read('lf-lo'); let b = read('lf-hi');
    if (!(s >= 1)) { fail('Sets needs a number.', 'lf-sets'); return; }
    if (!(a >= 1) && !(b >= 1)) { fail('Add a rep count.', 'lf-lo'); return; }
    if (!(a >= 1)) a = b;
    if (!(b >= 1)) b = a;
    if (a > b) [a, b] = [b, a];
    const r = read('lf-rest') > 0 ? Math.round(read('lf-rest')) : curRest;
    const t = trackingNow();
    const values = {
      name: n, muscle: isNew ? overlay.querySelector('#lf-muscle').value : muscle,
      equipment: curType === 'timed' ? 'bodyweight' : curType, tracking: t,
      sets: s, lo: a, hi: b, rest: r, inc: t === 'weight' ? (read('lf-inc') > 0 ? read('lf-inc') : (JUMP[curType] ?? 5)) : 0,
    };
    close();
    onSave?.(values);
  }

  paint();
}

// Default answers for a new lift of this muscle, from the lift it replaces if there is one.
export function liftFormDefaults(pe) {
  if (!pe) return { sets: 3, lo: 6, hi: 8, rest: 120 };
  return { muscle: pe.exercise?.muscle_group || 'chest', sets: pe.target_sets || 3, lo: pe.rep_low || 6, hi: pe.rep_high || 8, rest: pe.rest_seconds || 120 };
}
